import React from 'react'
import { connect } from 'react-redux'
import { Label } from 'semantic-ui-react'
// Local
import {
  formatIcd10Code,
  displayRiskCode
} from '../../common/formatUtils'
import { practitionerTagDisplayCheck } from '../../common/practitionerTagUtils'
import AddressedIcon from '../common/addressedIcon'
import PhysicianConditionHccHeaderToolbar from './physicianConditionHccHeaderToolbar'
import If from '../../common/if'
import { 
  EncounterConditionDisplay,
  EncounterConditionDisplayModified
} from '../../authenticated/types'
import { shouldRenderReskin2025 } from '../../../constant'
import { RootState } from '../../../store'

/**
 * Provides a component for the physician HCC condition group header title. The
 * title can include a toolbar and group properties
 *
 * @param mode View mode. Options: null or "smart". Default: null
 * @param active True if group is currently in active state
 * @param hccCode HCC Code
 * @param hccDescription HCC description
 * @param conditions Group conditions
 * @param onConditionAction Callback for condition action
 *        Arguments: action, condition
 */

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  active: boolean
  conditions: EncounterConditionDisplay[]
  hccCode: string
  hccDescription: string
  mode: 'smart' | null
  onConditionAction: (
    action: any,
    condition: EncounterConditionDisplay
  ) => void
}

function PhysicianConditionHccHeaderTitle(
  props: Props
): JSX.Element {
  const {
    active: isActiveProp,
    conditions: conditionsProp,
    currentUser,
    hccCode: hccCodeProp,
    hccDescription: hccDescriptionProp,
    mode: modeProp,
    onConditionAction: onConditionActionProp
  } = props

  const isHccGroup = true

  const shouldShowToolbar = practitionerTagDisplayCheck(
    currentUser,
    conditionsProp as unknown as EncounterConditionDisplayModified[],
    isHccGroup
  )

  const conditionTitle = shouldRenderReskin2025
    ? 'condition-title reskin'
    : 'condition-title'

  const labelColor = isActiveProp
    ? 'blue'
    : 'grey'

  const conditionHccTitle = () => {
    const filteredICD10Codes = conditionsProp.filter(
      (c: EncounterConditionDisplay) => (
        c &&
        !!c.icd10CodeJson &&
        c.icd10CodeJson.length > 0
      )
    )
    const formattedICD10Codes = filteredICD10Codes
      .map((cc: EncounterConditionDisplay) => formatIcd10Code(
        cc &&
        cc.icd10CodeJson &&
        cc.icd10CodeJson[0]
      ))
      .join(', ')
    return (
      <span>
        <Label
          pointing="right"
          color={ labelColor }
        >
          { displayRiskCode(hccCodeProp) }
        </Label>
        <span>&nbsp;</span>
        <span>
          <If isTrue={ isActiveProp }>
            <strong>
              { hccDescriptionProp }
            </strong>
          </If>
          <If isTrue={ !isActiveProp }>
            <span>
              { hccDescriptionProp }
            </span>
          </If>
        </span>
        <span>&nbsp;</span>
        <If isTrue={ formattedICD10Codes.length > 0 }>
          (<span className="italic">
            { formattedICD10Codes }
          </span>)
        </If>
      </span>
    )
  }

  const titleText = conditionHccTitle()

  if (modeProp && modeProp === 'smart') {
    return (
      <div className="physician-condition-title">
        <div className="flex-title">
          <AddressedIcon conditions={ conditionsProp } />
          <div className={ conditionTitle }>
            <If isTrue={ isActiveProp }>
              <strong>
                { titleText }
              </strong>
            </If>
            <If isTrue={ !isActiveProp }>
              titleText
            </If>
            <div className="condition-toolbar" />
          </div>
        </div>
        <If isTrue={ !!shouldShowToolbar }>
          <div className="m-1-top">
            <PhysicianConditionHccHeaderToolbar
              mode={ modeProp }
              conditions={ conditionsProp }
              onConditionAction={
                onConditionActionProp as unknown as (
                  action: any,
                  conditions: EncounterConditionDisplay[]
                ) => void
              }
            />
          </div>
        </If>
      </div>
    )
  }

  return (
    <div className="physician-condition-title">
      <div className="flex-title">
        <AddressedIcon conditions={ conditionsProp } />
        <div className={ conditionTitle }>
          <If isTrue={ isActiveProp }>
            <strong>
              { titleText }
            </strong>
          </If>
          <If isTrue={ !isActiveProp }>
            titleText
          </If>
        </div>
        <div className="condition-toolbar">
          <If isTrue={ !!shouldShowToolbar }>
            <PhysicianConditionHccHeaderToolbar
              mode={ modeProp }
              conditions={ conditionsProp }
              onConditionAction={
                onConditionActionProp as unknown as (
                  action: any,
                  conditions: EncounterConditionDisplay[]
                ) => void
              }
            />
          </If>
        </div>
      </div>
    </div>
  )
}

export default connect(mapStateToProps)(PhysicianConditionHccHeaderTitle)
